import {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { Token } from "../types/api";
import { Method, Methods } from "../utils/constants/CommonConstants";
import Api from "../utils/api/axios";

type FetchOptions<Res> = Omit<
  UseQueryOptions<Res, Error, Res, QueryKey>,
  "queryKey" | "queryFn"
>;

interface UseFetchDataConfig<Res, Req = undefined> {
  body?: Req;
  queryParams?: Record<string, any>;
  options?: FetchOptions<Res>;
  method?: Method;
  token: Token;
  enabled?: boolean;
  navigateToErrorPageIfThereisError?: boolean;
  staleTime?: number;
  cacheTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
}

const useFetchData = <Res, Req = undefined>(
  queryKey: QueryKey,
  endpoint: string,
  config?: UseFetchDataConfig<Res, Req>,
): UseQueryResult<Res, Error> => {
  const method = config?.method || Methods.GET;

  const filteredQueryParams = config?.queryParams
    ? Object.entries(config.queryParams)
        .filter(
          ([_, value]) =>
            value !== null &&
            value !== undefined &&
            value !== "" &&
            !Number.isNaN(value),
        )
        .map(([key, value]) => ({ [key]: value }))
    : undefined;

  return useQuery<Res, Error>({
    queryKey: queryKey,
    queryFn: () =>
      Api<Req, Res>({
        method,
        endpoint,
        body: config?.body,
        token: config?.token,
        navigateToErrorPageIfThereisError:
          config?.navigateToErrorPageIfThereisError,
        filter: filteredQueryParams,
      }),
    enabled: config?.enabled,
    staleTime: config?.staleTime ?? 0,
    refetchOnWindowFocus: config?.refetchOnWindowFocus,
    refetchOnMount: config?.refetchOnMount,
    ...config?.options,
  });
};

export default useFetchData;
